import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { GeminiHeader } from "@/components/GeminiHeader";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { VisualSteps } from "@/components/help/VisualSteps";
import { ArticleContent } from "@/components/help/ArticleContent";
import { Skeleton } from "@/components/ui/skeleton";
import { Volume2, BookOpen, ListChecks } from "lucide-react";
import { Button } from "@/components/ui/button";

interface StepContent {
  step: number;
  icon: string;
  title: string;
  description: string;
}

interface ArticleSection {
  type: "intro" | "heading" | "paragraph" | "list" | "quote" | "table";
  content?: string;
  title?: string;
  items?: string[];
  headers?: string[];
  rows?: string[][];
  author?: string;
}

interface HelpArticleData {
  id: string;
  slug: string;
  title: string;
  title_id: string;
  summary: string;
  summary_id: string;
  content: StepContent[] | { sections: ArticleSection[] };
  content_id: StepContent[] | { sections: ArticleSection[] };
  has_video: boolean;
  video_url: string | null;
  article_type: "steps" | "article";
}

const HelpArticle = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language } = useLanguage();
  const [article, setArticle] = useState<HelpArticleData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);

  useEffect(() => {
    if (slug) {
      loadArticle();
    }
  }, [slug]);

  const loadArticle = async () => {
    try {
      const { data } = await supabase
        .from("help_articles")
        .select("*")
        .eq("slug", slug)
        .eq("is_active", true)
        .maybeSingle();
      
      if (data) {
        setArticle({
          id: data.id,
          slug: data.slug,
          title: data.title,
          title_id: data.title_id,
          summary: data.summary,
          summary_id: data.summary_id,
          content: data.content as unknown as StepContent[] | { sections: ArticleSection[] },
          content_id: data.content_id as unknown as StepContent[] | { sections: ArticleSection[] },
          has_video: data.has_video,
          video_url: data.video_url,
          article_type: (data as any).article_type || "steps",
        });
      }
    } catch (error) {
      console.error("Error loading article:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSpeak = () => {
    if (!article) return;
    
    // Cancel if already speaking
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }

    const content = language === "id" ? article.content_id : article.content;
    const title = language === "id" ? article.title_id : article.title;
    
    // Build text to speak based on article type
    let textToSpeak = title + ". ";
    
    if (article.article_type === "article" && "sections" in content) {
      content.sections.forEach((section) => {
        if (section.content) textToSpeak += section.content + ". ";
        if (section.title) textToSpeak += section.title + ". ";
        if (section.items) textToSpeak += section.items.join(". ") + ". ";
      });
    } else if (Array.isArray(content)) {
      content.forEach((step, index) => {
        textToSpeak += `${language === "id" ? "Langkah" : "Step"} ${index + 1}: ${step.title}. ${step.description}. `;
      });
    }

    const utterance = new SpeechSynthesisUtterance(textToSpeak);
    utterance.lang = language === "id" ? "id-ID" : "en-US";
    utterance.rate = 0.9;
    
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    
    setIsSpeaking(true);
    window.speechSynthesis.speak(utterance);
  };

  // Cleanup speech on unmount
  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
    };
  }, []);

  const articleTitle = article 
    ? (language === "id" ? article.title_id : article.title)
    : (language === "id" ? "Artikel" : "Article");

  const content = article 
    ? (language === "id" ? article.content_id : article.content)
    : [];
  
  const isArticleType = article?.article_type === "article";
  const articleSections = isArticleType && "sections" in content ? content.sections : [];
  const steps = !isArticleType && Array.isArray(content) ? content : [];

  return (
    <div className="min-h-screen bg-background">
      <GeminiHeader title={articleTitle} showBack useHistoryBack />
      
      <div className="p-4 pb-24 max-w-lg mx-auto">
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-12 w-full rounded-full" />
            <Skeleton className="h-32 rounded-2xl" />
            <Skeleton className="h-32 rounded-2xl" />
            <Skeleton className="h-32 rounded-2xl" />
          </div>
        ) : article ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {/* Article Type Badge */}
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${isArticleType ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                {isArticleType ? <BookOpen className="w-4 h-4" /> : <ListChecks className="w-4 h-4" />}
                {isArticleType 
                  ? (language === "id" ? "Penjelasan" : "Explanation")
                  : (language === "id" ? "Panduan" : "Guide")}
              </div>
            </div>

            {/* Audio Button */}
            <Button
              variant="outline"
              size="lg"
              onClick={handleSpeak}
              className={`w-full h-14 rounded-full mb-6 gap-3 text-base ${isSpeaking ? 'bg-primary text-primary-foreground' : ''}`}
            >
              <Volume2 className={`h-5 w-5 ${isSpeaking ? 'animate-pulse' : ''}`} />
              {isSpeaking 
                ? (language === "id" ? "Berhenti" : "Stop") 
                : (language === "id" ? "Dengarkan" : "Listen")}
            </Button>

            {/* Summary */}
            <p className="text-muted-foreground text-center mb-6">
              {language === "id" ? article.summary_id : article.summary}
            </p>

            {/* Render based on article type */}
            {isArticleType ? (
              <ArticleContent sections={articleSections} />
            ) : (
              <VisualSteps steps={steps} />
            )}

            {/* Video if available */}
            {article.has_video && article.video_url && (
              <div className="mt-6">
                <h3 className="font-medium mb-3">
                  {language === "id" ? "Tonton Video" : "Watch Video"}
                </h3>
                <div className="aspect-video rounded-2xl overflow-hidden bg-muted">
                  <iframe
                    src={article.video_url}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            )}
          </motion.div>
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              {language === "id" ? "Artikel tidak ditemukan" : "Article not found"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HelpArticle;
